/**
 * ContentRegistry - Central registry for game content types and instances.
 *
 * Content packs use this registry to:
 * 1. Register new content types (e.g., "stats", "items", "skills")
 * 2. Register content instances of those types (e.g., HEALTH_STAT)
 *
 * The core pack registers types; subsequent packs add content.
 */

import { makeAutoObservable, runInAction } from 'mobx';

/**
 * Configuration for a content type.
 */
export interface ContentTypeConfig<T> {
  /** Type guard to validate items */
  validate: (item: unknown) => item is T;
  /** Called when an item is registered (optional) */
  onRegister?: (item: T) => void;
  /** Whether items can be overridden by later packs (default: false) */
  allowOverride?: boolean;
}

/**
 * Base interface for all content items.
 */
export interface ContentItem {
  /** Unique identifier for this content item */
  id: string;
}

/**
 * Internal storage for a content type.
 */
interface ContentTypeStorage {
  config: ContentTypeConfig<unknown>;
  items: Map<string, unknown>;
}

/**
 * ContentRegistry manages all game content.
 *
 * @example
 * ```ts
 * const registry = new ContentRegistry();
 *
 * // Core pack registers the type
 * registry.registerType('stats', { validate: isStatDefinition });
 *
 * // Any pack can register content
 * registry.register('stats', HEALTH_STAT);
 *
 * // Retrieve content
 * const health = registry.get<StatDefinition>('stats', 'health');
 * ```
 */
export class ContentRegistry {
  /** Storage for all content types */
  private types = new Map<string, ContentTypeStorage>();

  /** Whether the registry is locked (no more type registrations) */
  private locked = false;

  constructor() {
    /* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any */
    makeAutoObservable(this, {
      // Don't make private fields observable
      types: false,
      locked: false,
    } as any);
    /* eslint-enable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any */
  }

  /**
   * Register a new content type.
   *
   * Only the core pack should register types. Call lock() after
   * all types are registered to prevent accidental additions.
   *
   * @param typeName - Unique name for this content type
   * @param config - Configuration including validator
   * @throws Error if type already exists or registry is locked
   */
  registerType<T extends ContentItem>(typeName: string, config: ContentTypeConfig<T>): void {
    if (this.locked) {
      throw new Error(`Cannot register type "${typeName}": ContentRegistry is locked`);
    }

    if (this.types.has(typeName)) {
      throw new Error(`Content type "${typeName}" is already registered`);
    }

    this.types.set(typeName, {
      config: config as ContentTypeConfig<unknown>,
      items: new Map(),
    });
  }

  /**
   * Lock the registry to prevent new type registrations.
   *
   * Call this after the core pack has registered all types.
   */
  lock(): void {
    this.locked = true;
  }

  /**
   * Check if the registry is locked.
   */
  get isLocked(): boolean {
    return this.locked;
  }

  /**
   * Register a content item.
   *
   * @param typeName - The content type to register under
   * @param item - The content item (must have an id property)
   * @throws Error if type doesn't exist, item is invalid, or duplicate ID
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  register<T extends ContentItem>(typeName: string, item: T): void {
    const storage = this.types.get(typeName);

    if (!storage) {
      throw new Error(
        `Content type "${typeName}" is not registered. ` +
          `Available types: ${Array.from(this.types.keys()).join(', ') || 'none'}`
      );
    }

    if (!storage.config.validate(item)) {
      throw new Error(`Invalid content item for type "${typeName}": validation failed`);
    }

    if (storage.items.has(item.id) && !storage.config.allowOverride) {
      throw new Error(
        `Content item "${item.id}" already exists in type "${typeName}". ` +
          `Set allowOverride: true to allow overwriting.`
      );
    }

    runInAction(() => {
      storage.items.set(item.id, item);
    });

    // Call onRegister hook if provided
    if (storage.config.onRegister) {
      storage.config.onRegister(item);
    }
  }

  /**
   * Register multiple content items at once.
   *
   * @param typeName - The content type to register under
   * @param items - Array of content items
   */
  registerAll<T extends ContentItem>(typeName: string, items: T[]): void {
    for (const item of items) {
      this.register(typeName, item);
    }
  }

  /**
   * Get a single content item by ID.
   *
   * @param typeName - The content type
   * @param id - The item ID
   * @returns The item, or undefined if not found
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  get<T extends ContentItem>(typeName: string, id: string): T | undefined {
    const storage = this.types.get(typeName);

    if (!storage) {
      return undefined;
    }

    return storage.items.get(id) as T | undefined;
  }

  /**
   * Get all content items of a type.
   *
   * @param typeName - The content type
   * @returns Array of all items (empty if type doesn't exist)
   */
  getAll<T extends ContentItem>(typeName: string): T[] {
    const storage = this.types.get(typeName);

    if (!storage) {
      return [];
    }

    return Array.from(storage.items.values()) as T[];
  }

  /**
   * Check if a content type is registered.
   *
   * @param typeName - The type name to check
   */
  hasType(typeName: string): boolean {
    return this.types.has(typeName);
  }

  /**
   * Check if a content item exists.
   *
   * @param typeName - The content type
   * @param id - The item ID
   */
  has(typeName: string, id: string): boolean {
    const storage = this.types.get(typeName);
    return storage?.items.has(id) ?? false;
  }

  /**
   * Get count of items in a content type.
   *
   * @param typeName - The content type
   */
  count(typeName: string): number {
    const storage = this.types.get(typeName);
    return storage?.items.size ?? 0;
  }

  /**
   * Get all registered type names.
   */
  getTypeNames(): string[] {
    return Array.from(this.types.keys());
  }

  /**
   * Clear all content (but keep types).
   *
   * Useful for testing or resetting game state.
   */
  clearContent(): void {
    runInAction(() => {
      for (const storage of this.types.values()) {
        storage.items.clear();
      }
    });
  }

  /**
   * Reset the registry completely.
   *
   * Clears all types and content. Use with caution.
   */
  reset(): void {
    runInAction(() => {
      this.types.clear();
      this.locked = false;
    });
  }
}
